import api from './api';
import { reportError } from '../utils/reportError';

const SW_PATH = `${process.env.PUBLIC_URL}/sw.js`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

const PushNotificationService = {
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  },

  async getOrRegisterSW() {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_PATH);
  },

  getSubscribedMasterId() {
    const stored = localStorage.getItem('pushMasterId');
    return stored ? Number(stored) : null;
  },

  async fetchVapidKey() {
    const { data } = await api.get('/api/push/vapid-public-key');
    return data.publicKey ?? data.public_key ?? data.key ?? data.vapidPublicKey ?? Object.values(data)[0];
  },

  async subscribe(masterId) {
    if (!this.isSupported()) throw new Error('Push не підтримується браузером');

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('permission_denied');

    const reg = await this.getOrRegisterSW();
    const vapidKey = await this.fetchVapidKey();

    const rawSub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const sub = rawSub.toJSON();
    await api.post('/api/push/subscribe', {
      masterId,
      endpoint: sub.endpoint,
      p256dh: sub.keys.p256dh,
      auth: sub.keys.auth,
    });

    localStorage.setItem('pushMasterId', String(masterId));
  },

  async reportPwaInstalled(masterId) {
    try {
      await api.post('/api/push/pwa-installed', { masterId });
    } catch {
      // silent — не критично
    }
  },

  async unsubscribe() {
    if (!this.isSupported()) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (!reg) return;
      const subscription = await reg.pushManager.getSubscription();
      if (!subscription) return;
      await api.delete('/api/push/subscribe', { data: { endpoint: subscription.endpoint } });
      await subscription.unsubscribe();
      localStorage.removeItem('pushMasterId');
    } catch (err) {
      reportError(err, { where: 'PushNotificationService.unsubscribe' });
    }
  },
};

export default PushNotificationService;
